import {
  type CSSProperties,
  type PropsWithChildren,
  useRef,
  useState,
} from 'react'
import Draggable, { type DraggableEvent } from 'react-draggable'

import { useBoundStore } from '../../store/store'
import {
  Copyrights,
  CustomerServiceButton,
  FooterLinks,
  GoUp,
  InboxButton,
  Navbar,
  Sponsors,
  ThemeSwitch,
  TopAd,
} from '../common'
import DownloadApps from '../common/download-apps'

interface DesktopLayoutProps {
  hideScrollUp?: boolean
  hideChat?: boolean
  hideInbox?: boolean
  style?: CSSProperties
  hideBalance?: boolean
}

const DesktopLayout = ({
  children,
  style,
  hideScrollUp,
  hideChat,
  hideInbox,
  hideBalance,
}: PropsWithChildren<DesktopLayoutProps>) => {
  const setIsChatVisible = useBoundStore(state => state.common.setIsChatVisible)
  const setTheme = useBoundStore(state => state.common.setTheme)
  const theme = useBoundStore(state => state.common.theme)
  const nodeRef = useRef(null)
  const [position, setPosition] = useState({ clientX: 0, clientY: 0 })
  const [timeStamp, setTimeStamp] = useState(0)

  const onStop = (e: DraggableEvent) => {
    const clientX = (e as MouseEvent).clientX
    const clientY = (e as MouseEvent).clientY
    const dragTime = e.timeStamp - timeStamp
    if (
      (clientX === position.clientX && clientY === position.clientY) ||
      dragTime < 100
    ) {
      const { id } = e.target as HTMLDivElement
      if (id === 'float-message-button') {
        window.open('https://t.me/MexplayCsr', '_blank')
      } else if (id === 'float-customer-service') {
        setIsChatVisible(true)
      } else if (id === 'go-up') {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: 'smooth',
        })
      } else if (id === 'theme-switch') {
        if (theme === 'light') {
          setTheme('dark')
          localStorage.setItem('theme', 'dark')
          document.body.classList.remove('theme-light')
          !document.body.classList.contains('theme-dark') &&
            document.body.classList.add('theme-dark')
        } else {
          setTheme('light')
          localStorage.setItem('theme', 'light')
          document.body.classList.remove('theme-dark')
          !document.body.classList.contains('theme-light') &&
            document.body.classList.add('theme-light')
        }
      }
    }
  }

  const onStart = (e: DraggableEvent) => {
    const { clientX, clientY, timeStamp } = e as MouseEvent
    setPosition({ clientX: clientX, clientY: clientY })
    setTimeStamp(timeStamp)
  }

  return (
    <div style={style}>
      <div
        className="fixed -z-10 opacity-0 pointer-events-none w-screen h-screen left-0 top-0"
        id="drag-boundary"
      />
      <TopAd />
      <Navbar hideBalance={hideBalance} />
      <Draggable
        onStart={onStart}
        onStop={onStop}
        axis="y"
        bounds="#drag-boundary"
        nodeRef={nodeRef}>
        <div
          ref={nodeRef}
          className="v-flx-ctr fixed z-[15] right-2.5 bottom-2.5">
          {!hideScrollUp && <GoUp />}
          {!hideInbox && <InboxButton />}
          {!hideChat && <CustomerServiceButton />}
          <ThemeSwitch theme={theme} />
        </div>
      </Draggable>
      {children}
      <div className="relative mt-[30px] bg-[#eaecf3] dark:bg-[#3b3e49] dark pt-[70px] pb-20">
        <div className="absolute w-full h-[26px] bg-braid bg-center left-0 top-0" />
        <div className="max-w-screen-xl mx-auto my-0">
          <FooterLinks />
          <DownloadApps />
          <Sponsors />
          <Copyrights />
        </div>
      </div>
    </div>
  )
}

export default DesktopLayout
